import Tableview from '../../components/commos/Tableview'
import NavigationHeader from '../../components/navigation/NavigationHeader';
import { skuService, type SKUListItem } from '../../services/skuService';
import { useSKUStore } from '../../stores';
import { useAlertStore } from '../../stores/alertStore';
import Loading from '../../features/sso-auth/components/Loading';
import { Button } from '@ab-inbev-maz/compass';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useTableData } from '../../hooks/useTableData';
import { useCallback } from 'react';

const POLL_INTERVAL = 3000;

const TablePage = () => {
  const { getListSkus, deleteSKU } = useSKUStore();
  const showAlert = useAlertStore(state => state.showAlert);
  const navigate = useNavigate();
  const handleCreateSKU = () => navigate('/skus');
  const handleEdit = (id: string | number) => navigate(`/skus/${id}`);

  const onDownloadItems = useCallback(async (selectedIds: (string | number)[]) => {
    const skuIds = selectedIds.length > 0 ? selectedIds : undefined;

    const exportResult = await skuService.exportSKUs(skuIds);
    if (!exportResult.success || !exportResult.task_id) {
      showAlert({
        text: 'Error al iniciar descarga',
        message: exportResult.message || 'No se pudo iniciar la exportación',
        status: 'error',
      });
      return;
    }

    const taskId = exportResult.task_id;

    const pollStatus = async (): Promise<string | null> => {
      let status = 'pending';
      while (status === 'pending' || status === 'started') {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        const statusResult = await skuService.getExportStatus(taskId);
        status = statusResult.status;
        if (status === 'success') return statusResult.download_url || null;
        if (status === 'failed') {
          showAlert({
            text: 'Error al generar archivo',
            message: statusResult.error || 'La exportación falló',
            status: 'error',
          });
          return null;
        }
      }
      return null;
    };

    const downloadUrl = await pollStatus();
    if (!downloadUrl) return;

    const blob = await skuService.downloadExport(taskId);
    if (!blob) {
      showAlert({
        text: 'Error al descargar archivo',
        message: 'No se pudo descargar el archivo generado',
        status: 'error',
      });
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `skus_export.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showAlert({
      text: 'Descarga completada',
      message: `Se descargaron ${skuIds ? skuIds.length + ' SKUs seleccionados' : 'todos los SKUs del país'}`,
      status: 'success',
    });
  }, [showAlert]);

  const {
    datos,
    loading,
    currentPage,
    totalCount,
    pageSize,
    searchValue,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleDelete,
    handleDownload,
    totalPages,
  } = useTableData<SKUListItem>({
    fetchData: getListSkus,
    deleteItems: deleteSKU,
    onDownloadItems,
    itemType: 'SKUs',
  });

  const columnas = [
    {
      title: 'Miniatura',
      key: 'image',
      render: (value: string) => <img src={value} style={{ height: '70px', objectFit: "contain" }} />
    },
    {
      key: 'name',
      title: 'Nombre',
    },
    {
      key: 'sku_number',
      title: 'ID de SKU',
    },
    {
      key: 'brand',
      title: 'Marca',
    },
    {
      key: 'presentation',
      title: 'Presentación',
    },
    {
      key: 'size',
      title: 'Talla',
    },
    {
      key: 'volume',
      title: 'Volumen',
    },
    {
      key: 'quantity',
      title: 'Cantidad',
    },
    {
      key: 'tipology',
      title: 'Tipología',
    },
    {
      key: 'created_at',
      title: 'Fecha de creación',
      render: (value: string) => DateTime.fromISO(value).toFormat('dd/MM/yyyy'),
    },
    {
      key: 'actions',
      title: 'Acciones',
      render: (t: undefined, data: SKUListItem) => (
        <Button label='Editar' rightIcon='functionalities/edit-2' onClick={handleEdit.bind(null, data.id)} />
      )
    }
  ];

  const handleDeleteSkus = (rows: string[]) => {
    const skuIds = datos
      .filter((item: any) => rows.includes(item.id))
      .map((item: any) => item.data.sku_number);
    handleDelete(rows, skuIds);
  }

  // Mostrar loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando los SKUs." />
        </div>
      </div>
    );
  }


  return (
    <>
      <NavigationHeader
        title='Librería de SKU'
        description='Desde aquí puedes consultar todos los SKUs guardados y crear nuevos cuando lo necesites.'
        backLink='/'
        actions={[
          <Button
            label="Crear nuevo SKU"
            rightIcon="shopping/beer"
            variant="primary"
            onClick={handleCreateSKU}
            className="cursor-pointer"
            data-testid="action-button"
          />
        ]}
      />
      <Tableview
        title='Librería de SKU'
        datos={datos}
        columnas={columnas}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        showDeleteButton={true}
        onDelete={handleDeleteSkus}
        onDownload={handleDownload}
      />
    </>
  )
}

export default TablePage

